import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { api } from '../api';
import { canViewActivityLog } from '../permissions';
import { formatBidNumber } from '../utils/bidNumber';

const ACTIVITY_TYPE_COLORS = {
  Edit: '#3b82f6',
  Note: '#6b7280',
  Compliance: '#22c55e',
  StatusChange: '#a855f7',
};

const PAGE_SIZE = 50;

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function TypeBadge({ type }) {
  const color = ACTIVITY_TYPE_COLORS[type] || '#6b7280';
  return (
    <span style={{
      display: 'inline-block', fontSize: '11px', padding: '2px 8px', borderRadius: '4px',
      fontWeight: 600, backgroundColor: `${color}18`, color, border: `1px solid ${color}40`,
    }}>{type}</span>
  );
}

export default function LogViewer() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canView = canViewActivityLog(user);

  const [activeTab, setActiveTab] = useState('all');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Refs let the IntersectionObserver read fresh paging state without re-arming.
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectActivities, setProjectActivities] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);

  const [errors, setErrors] = useState([]);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [errorSummary, setErrorSummary] = useState(null);
  const [errorOffset, setErrorOffset] = useState(0);
  const [hasMoreErrors, setHasMoreErrors] = useState(true);

  useEffect(() => {
    if (canView) api.getProjects().then(setProjects).catch(console.error);
  }, [canView]);

  const loadActivity = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const newOffset = reset ? 0 : offset;
      const params = { limit: PAGE_SIZE, offset: newOffset };
      if (typeFilter) params.type = typeFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const data = await api.getActivityLog(params);
      if (reset) { setActivities(data); setOffset(PAGE_SIZE); }
      else { setActivities(prev => [...prev, ...data]); setOffset(prev => prev + PAGE_SIZE); }
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) { console.error('Failed to load activity log:', err); }
    finally { setLoading(false); }
  }, [offset, typeFilter, debouncedSearch, fromDate, toDate]);

  // Debounce the free-text search so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 250);
    return () => clearTimeout(t);
  }, [searchText]);

  // Reset + reload whenever any server-side filter changes.
  useEffect(() => {
    if (activeTab === 'all' && canView) loadActivity(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, typeFilter, debouncedSearch, fromDate, toDate, canView]);

  // Infinite scroll: when the bottom sentinel comes into view, fetch the next
  // page. Refs hold live loading/hasMore so the observer never double-fires.
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => {
    if (activeTab !== 'all' || !canView) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingRef.current) loadActivity(false);
      },
      { rootMargin: '300px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [activeTab, canView, loadActivity]);

  useEffect(() => {
    if (activeTab === 'project' && selectedProjectId) {
      setProjectLoading(true);
      api.getActivity(selectedProjectId)
        .then(setProjectActivities)
        .catch(console.error)
        .finally(() => setProjectLoading(false));
    }
  }, [activeTab, selectedProjectId]);

  const loadErrors = useCallback(async (reset = false) => {
    setErrorsLoading(true);
    try {
      const newOffset = reset ? 0 : errorOffset;
      const data = await api.getErrors({ limit: PAGE_SIZE, offset: newOffset });
      if (reset) { setErrors(data); setErrorOffset(PAGE_SIZE); }
      else { setErrors(prev => [...prev, ...data]); setErrorOffset(prev => prev + PAGE_SIZE); }
      setHasMoreErrors(data.length === PAGE_SIZE);
    } catch (err) { console.error('Failed to load errors:', err); }
    finally { setErrorsLoading(false); }
  }, [errorOffset]);

  useEffect(() => {
    if (activeTab === 'errors' && canView) {
      loadErrors(true);
      api.getErrorSummary().then(setErrorSummary).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canView]);

  if (!canView) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <h3>Access Denied</h3>
          <p>You do not have permission to view the activity log.</p>
        </div>
      </div>
    );
  }

  // "All Activity" is filtered server-side (search/date), so render `activities`
  // directly. The per-project tab loads one project fully, so it keeps a simple
  // client-side description filter.
  const filteredProjectActivities = searchText
    ? projectActivities.filter(a => (a.Description || '').toLowerCase().includes(searchText.toLowerCase()))
    : projectActivities;

  // One-line truncation so a long description can't widen the page; the full
  // text is available on hover, and clicking the project opens detail.
  const ellipsis = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  const renderTable = (data, showProject = true) => (
    <div className="table-wrapper">
      <table className="data-table" style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: '170px' }}>Date/Time (PST)</th>
            {showProject && <th style={{ width: '220px' }}>Project</th>}
            <th style={{ width: '110px' }}>Type</th>
            <th>Description</th>
            <th style={{ width: '130px' }}>Changed By</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr><td colSpan={showProject ? 5 : 4} className="empty-state">No activity found.</td></tr>
          ) : data.map((a, i) => (
            <tr key={a.ActivityId || i}>
              <td style={{ whiteSpace: 'nowrap', fontSize: '13px', color: '#555' }}>{formatDate(a.CreatedAt)}</td>
              {showProject && (
                <td style={ellipsis} title={`${formatBidNumber(a.BidNumber, a.RevisionNumber)} - ${a.ProjectName}`}>
                  <span style={{ color: 'var(--color-accent)', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}
                    onClick={() => navigate(`/projects/${a.ProjectId}`)}>
                    {formatBidNumber(a.BidNumber, a.RevisionNumber)} - {a.ProjectName}
                  </span>
                </td>
              )}
              <td><TypeBadge type={a.ActivityType} /></td>
              <td style={{ fontSize: '13px', ...ellipsis }} title={a.Description}>{a.Description}</td>
              <td style={{ fontSize: '13px', color: '#555', ...ellipsis }}>{a.CreatedBy || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderLoadMore = (hasMoreFlag, loadingFlag, loadFn) => (
    hasMoreFlag && (
      <div style={{ padding: '16px', textAlign: 'center', borderTop: '1px solid var(--color-gray-200)' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => loadFn(false)} disabled={loadingFlag}>
          {loadingFlag ? 'Loading...' : 'Load More'}
        </button>
      </div>
    )
  );

  return (
    <>
      <div className="top-bar">
        <h2>Activity Log</h2>
      </div>

      <div className="page-content">
        <div className="ua-tabs">
          <button className={`ua-tab ${activeTab === 'all' ? 'ua-tab-active' : ''}`} onClick={() => setActiveTab('all')}>All Activity</button>
          <button className={`ua-tab ${activeTab === 'project' ? 'ua-tab-active' : ''}`} onClick={() => setActiveTab('project')}>By Project</button>
          <button className={`ua-tab ${activeTab === 'errors' ? 'ua-tab-active' : ''}`} style={activeTab === 'errors' ? { borderBottomColor: '#dc2626', color: '#dc2626' } : {}}
            onClick={() => setActiveTab('errors')}>
            Server Errors{errorSummary?.last24h > 0 ? ` (${errorSummary.last24h})` : ''}
          </button>
        </div>

        <div className="card" style={{ padding: '12px 16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            {activeTab === 'all' && (
              <select className="filter-select" value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setOffset(0); }}>
                <option value="">All Types</option>
                <option value="Compliance">Compliance</option>
                <option value="Edit">Edit</option>
                <option value="Note">Note</option>
                <option value="StatusChange">StatusChange</option>
              </select>
            )}
            <input type="text" className="filter-input"
              placeholder={activeTab === 'all' ? 'Search bid #, project, description…' : 'Search descriptions...'}
              value={searchText}
              onChange={e => setSearchText(e.target.value)} style={{ flex: 1, minWidth: '200px' }} />
            {activeTab === 'all' && (
              <>
                <label style={{ fontSize: '12px', color: '#666' }}>From
                  <input type="date" className="filter-input" value={fromDate}
                    onChange={e => setFromDate(e.target.value)} style={{ marginLeft: '6px' }} />
                </label>
                <label style={{ fontSize: '12px', color: '#666' }}>To
                  <input type="date" className="filter-input" value={toDate}
                    onChange={e => setToDate(e.target.value)} style={{ marginLeft: '6px' }} />
                </label>
                {(fromDate || toDate || searchText) && (
                  <button className="btn btn-secondary btn-sm"
                    onClick={() => { setSearchText(''); setFromDate(''); setToDate(''); }}>Clear</button>
                )}
              </>
            )}
            {activeTab === 'project' && (
              <select className="filter-select" value={selectedProjectId}
                onChange={e => setSelectedProjectId(e.target.value)} style={{ minWidth: '280px' }}>
                <option value="">Select a project...</option>
                {projects.map(p => (
                  <option key={p.ProjectId} value={p.ProjectId}>
                    {formatBidNumber(p.BidNumber, p.RevisionNumber)} — {p.ProjectName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="card" style={{ padding: 0 }}>
          {activeTab === 'all' && (
            <>
              {loading && activities.length === 0
                ? <div className="empty-state">Loading...</div>
                : <>
                    {renderTable(activities, true)}
                    <div ref={sentinelRef} aria-hidden="true" />
                    {loading && activities.length > 0 && (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#888', fontSize: '13px' }}>Loading…</div>
                    )}
                    {!hasMore && activities.length > 0 && (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>End of activity</div>
                    )}
                  </>
              }
            </>
          )}

          {activeTab === 'project' && (
            <>
              {!selectedProjectId
                ? <div className="empty-state">Select a project above to view its activity.</div>
                : projectLoading
                  ? <div className="empty-state">Loading...</div>
                  : renderTable(filteredProjectActivities, false)
              }
            </>
          )}

          {activeTab === 'errors' && (
            <>
              {errorSummary && (
                <div style={{ display: 'flex', gap: '24px', padding: '12px 16px', borderBottom: '1px solid var(--color-gray-200)', fontSize: '13px', color: '#555', background: 'var(--color-gray-100)' }}>
                  <span>Last 24h: <strong style={{ color: errorSummary.last24h > 0 ? '#dc2626' : '#22c55e' }}>{errorSummary.last24h}</strong></span>
                  <span>Last 7d: <strong>{errorSummary.last7d}</strong></span>
                  <span>Last 30d: <strong>{errorSummary.last30d}</strong></span>
                  <span>Total: <strong>{errorSummary.total}</strong></span>
                </div>
              )}
              {errorsLoading && errors.length === 0
                ? <div className="empty-state">Loading...</div>
                : errors.length === 0
                  ? <div style={{ padding: '40px', textAlign: 'center', color: '#22c55e', fontWeight: 500 }}>No errors recorded.</div>
                  : (
                    <>
                      <div className="table-wrapper">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th style={{ width: '170px' }}>Date/Time (PST)</th>
                              <th style={{ width: '80px' }}>Severity</th>
                              <th style={{ width: '200px' }}>Source</th>
                              <th>Message</th>
                              <th style={{ width: '100px' }}>User</th>
                            </tr>
                          </thead>
                          <tbody>
                            {errors.map((e, i) => (
                              <tr key={e.ErrorId || i}>
                                <td style={{ whiteSpace: 'nowrap', fontSize: '13px', color: '#555' }}>{formatDate(e.CreatedAt)}</td>
                                <td>
                                  <span style={{
                                    fontSize: '11px', padding: '2px 8px', borderRadius: '4px', fontWeight: 600,
                                    backgroundColor: e.Severity === 'critical' ? '#dc262618' : '#ef444418',
                                    color: e.Severity === 'critical' ? '#dc2626' : '#ef4444',
                                    border: `1px solid ${e.Severity === 'critical' ? '#dc262640' : '#ef444440'}`,
                                  }}>{e.Severity}</span>
                                </td>
                                <td style={{ fontSize: '12px', fontFamily: 'monospace', color: '#555' }}>{e.RequestMethod ? `${e.RequestMethod} ` : ''}{e.Source}</td>
                                <td style={{ fontSize: '13px' }}>{e.Message}</td>
                                <td style={{ fontSize: '13px', color: '#555' }}>{e.Username || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {renderLoadMore(hasMoreErrors, errorsLoading, loadErrors)}
                    </>
                  )
              }
            </>
          )}
        </div>
      </div>
    </>
  );
}
